import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Plus, Search, Edit, Trash2, UserCheck, UserX, Key, RotateCcw, AlertTriangle } from 'lucide-react';
import { usersAPI } from '@shared/apiClient';
import { formatDate, getStatusColor, getStatusText } from '@shared/utils';
import UserFormModal from '../components/UserFormModal';
import toast from 'react-hot-toast';

export default function Users() {
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [passwordResetModal, setPasswordResetModal] = useState({ isOpen: false, userId: null, newPassword: '' });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['users', page, searchTerm],
    () => usersAPI.getUsers({ page, limit: 20, search: searchTerm }),
    { keepPreviousData: true, enabled: activeTab === 'active' }
  );

  const { data: deletedData, isLoading: deletedLoading } = useQuery(
    ['deletedUsers', page, searchTerm],
    () => usersAPI.getDeletedUsers({ page, limit: 20, search: searchTerm }),
    { keepPreviousData: true, enabled: activeTab === 'deleted' }
  );

  const deactivateMutation = useMutation(usersAPI.deactivateUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      toast.success('User deactivated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to deactivate user');
    },
  });

  const reactivateMutation = useMutation(usersAPI.reactivateUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      toast.success('User reactivated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reactivate user');
    },
  });

  const deleteMutation = useMutation(usersAPI.deleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      toast.success('User deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    },
  });

  const resetPasswordMutation = useMutation(
    ({ userId, newPassword }) => usersAPI.resetPassword(userId, newPassword),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users');
        toast.success('Password reset successfully');
        setPasswordResetModal({ isOpen: false, userId: null });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reset password');
      },
    }
  );

  const handleDeactivate = (userId) => {
    if (window.confirm('Are you sure you want to deactivate this user?')) {
      deactivateMutation.mutate(userId);
    }
  };

  const handleReactivate = (userId) => {
    reactivateMutation.mutate(userId);
  };

  const undeleteMutation = useMutation(usersAPI.undeleteUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('deletedUsers');
      queryClient.invalidateQueries('users');
      toast.success('User restored successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to restore user');
    },
  });

  const handleUndelete = (userId, userName) => {
    if (window.confirm(`Are you sure you want to restore ${userName}?`)) {
      undeleteMutation.mutate(userId);
    }
  };

  const handleDelete = (userId, userName) => {
    if (window.confirm(`Are you sure you want to delete ${userName}? This action cannot be undone. The user will be hidden from all views but can be restored from the Deleted Users tab.`)) {
      deleteMutation.mutate(userId);
    }
  };

  const handleResetPassword = (userId) => {
    setPasswordResetModal({ isOpen: true, userId, newPassword: '' });
  };

  const handlePasswordResetSubmit = (e) => {
    e.preventDefault();
    if (passwordResetModal.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    resetPasswordMutation.mutate({
      userId: passwordResetModal.userId,
      newPassword: passwordResetModal.newPassword
    });
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  if ((activeTab === 'active' && isLoading) || (activeTab === 'deleted' && deletedLoading)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="hub-page-header flex justify-between items-center">
        <div>
          <h1>Users</h1>
          <p>Manage system users and their permissions</p>
        </div>
        <button 
          onClick={handleCreateUser}
          className="btn btn-primary"
          disabled={activeTab === 'deleted'}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
      <div className="border-b border-surface-border">
        <nav className="flex -mb-px space-x-6">
          <button
            type="button"
            onClick={() => { setActiveTab('active'); setPage(1); }}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Users
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('deleted'); setPage(1); }}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'deleted'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Deleted Users
          </button>
        </nav>
      </div>

      {activeTab === 'deleted' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Deleted Users</h3>
              <p className="mt-1 text-sm text-yellow-700">
                Users marked as deleted are hidden from all views. Restore them here if needed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === 'deleted' ? 'Search deleted users...' : 'Search users...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
      </div>

      {/* Users Table */}
      {activeTab === 'active' ? (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data?.users?.map((user) => (
                <tr key={user.id}>
                  <td className="font-medium">
                    {user.firstName} {user.lastName}
                  </td>
                  <td className="font-mono text-sm">{user.username}</td>
                  <td>{user.email || <span className="text-gray-400">—</span>}</td>
                  <td>
                    <span className="capitalize">{user.role}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusColor(user.isActive ? 'active' : 'inactive')}`}>
                      {getStatusText(user.isActive ? 'active' : 'inactive')}
                    </span>
                  </td>
                  <td>{formatDate(user.lastLogin)}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditUser(user)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit user"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleResetPassword(user.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Reset password"
                      >
                        <Key className="h-4 w-4" />
                      </button>
                      {user.isActive ? (
                        <button
                          onClick={() => handleDeactivate(user.id)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Deactivate user"
                        >
                          <UserX className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivate(user.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Reactivate user"
                        >
                          <UserCheck className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(user.id, `${user.firstName} ${user.lastName}`)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      ) : (
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Deleted Date</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {deletedData?.data?.users?.length > 0 ? (
                deletedData.data.users.map((user) => (
                  <tr key={user.id}>
                    <td className="font-medium">
                      {user.firstName} {user.lastName}
                    </td>
                    <td>{user.email}</td>
                    <td><span className="capitalize">{user.role}</span></td>
                    <td>{formatDate(user.updatedAt)}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <button
                        onClick={() => handleUndelete(user.id, `${user.firstName} ${user.lastName}`)}
                        className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                        disabled={undeleteMutation.isLoading}
                      >
                        <RotateCcw className="h-4 w-4" />
                        <span>Restore</span>
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No deleted users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pagination */}
      {(activeTab === 'active' ? data?.data?.pagination : deletedData?.data?.pagination) && (() => {
        const pagination = activeTab === 'active' ? data.data.pagination : deletedData.data.pagination;
        return (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {pagination.start} to {pagination.end} of{' '}
            {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={!pagination.hasPrev}
              className="btn btn-secondary btn-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={!pagination.hasNext}
              className="btn btn-secondary btn-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        );
      })()}

      {/* User Form Modal */}
      <UserFormModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
      />

      {/* Password Reset Modal */}
      {passwordResetModal.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Reset Password
              </h3>
              <form onSubmit={handlePasswordResetSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordResetModal.newPassword}
                    onChange={(e) => setPasswordResetModal({ ...passwordResetModal, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new password (min 6 characters)"
                    required
                    minLength={6}
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={resetPasswordMutation.isLoading}
                    className="flex-1 btn btn-primary disabled:opacity-50"
                  >
                    {resetPasswordMutation.isLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPasswordResetModal({ isOpen: false, userId: null, newPassword: '' })}
                    className="flex-1 btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
